import React from 'react';
import type { DrumInstrument } from '../../types';
import { DRUM_TYPES } from '../../config/drumKits';
import { getSamplesByType } from '../../config/drumSamples';

interface InstrumentFormProps {
  instrument: DrumInstrument;
  index: number;
  instrumentsLength: number;
  updateInstrument: (index: number, field: string, value: string) => void;
  removeInstrument: (index: number) => void;
  moveInstrumentUp: (index: number) => void;
  moveInstrumentDown: (index: number) => void;
  previewSample: (sampleId: string, index: number) => void;
  playingPreviews: Record<string, boolean>;
  getThemeClass: (type: string) => string;
  t: (key: string) => string;
}

export default function InstrumentForm({
  instrument,
  index,
  instrumentsLength,
  updateInstrument,
  removeInstrument,
  moveInstrumentUp,
  moveInstrumentDown,
  previewSample,
  playingPreviews,
  getThemeClass,
  t,
}: InstrumentFormProps): JSX.Element {
  return (
    <div className={`p-3 rounded border-2 ${getThemeClass('border')} ${instrument.color} bg-opacity-20`}>
      {/* Desktop layout */}
      <div className="hidden md:grid md:grid-cols-7 gap-3 items-center">
        <div className="text-center">
          <label className="block text-xs font-medium mb-1">{t('order')}</label>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white border-2 border-white shadow-md mx-auto ${instrument.color}`}>{instrument.id}</div>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('instrumentType')}</label>
          <select
            value={instrument.type}
            onChange={(e) => updateInstrument(index, 'type', e.target.value)}
            className={`w-full border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('input')}`}
          >
            {DRUM_TYPES.map(type => (
              <option key={type.id} value={type.id}>{t(`drumTypes.${type.id}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('instrumentId')}</label>
          <input
            type="text"
            value={instrument.id}
            onChange={(e) => updateInstrument(index, 'id', e.target.value.toUpperCase())}
            className={`w-full border px-2 py-1 rounded font-mono ${getThemeClass('border')} ${getThemeClass('input')}`}
            maxLength={3}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('instrumentName')}</label>
          <input
            type="text"
            value={instrument.name}
            onChange={(e) => updateInstrument(index, 'name', e.target.value)}
            className={`w-full border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('input')}`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('sample')}</label>
          <select
            value={instrument.sampleId || (instrument as any).sample}
            onChange={(e) => updateInstrument(index, 'sampleId', e.target.value)}
            className={`w-full border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('input')}`}
          >
            {getSamplesByType(instrument.type).map((sample: any) => (
              <option key={sample.id} value={sample.id}>{sample.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('color')}</label>
          <select
            value={instrument.color}
            onChange={(e) => updateInstrument(index, 'color', e.target.value)}
            className={`w-full border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('input')}`}
          >
            <option value="bg-green-600">{t('drumTypes.kick')}</option>
            <option value="bg-red-600">{t('drumTypes.snare')}</option>
            <option value="bg-red-400">{t('drumTypes.snare')} (Light)</option>
            <option value="bg-yellow-600">{t('drumTypes.hihat')}</option>
            <option value="bg-yellow-500">{t('drumTypes.hihat')} (Medium)</option>
            <option value="bg-yellow-300">{t('drumTypes.openhat')}</option>
            <option value="bg-blue-400">{t('drumTypes.tom')} (Light)</option>
            <option value="bg-blue-500">{t('drumTypes.tom')}</option>
            <option value="bg-blue-700">{t('drumTypes.tom')} (Dark)</option>
            <option value="bg-purple-400">{t('drumTypes.clap')}</option>
            <option value="bg-purple-300">{t('drumTypes.clap')} (Light)</option>
            <option value="bg-orange-600">{t('drumTypes.crash')}</option>
            <option value="bg-orange-400">{t('drumTypes.ride')}</option>
            <option value="bg-orange-300">{t('drumTypes.crash')} (Light)</option>
            <option value="bg-pink-500">{t('drumTypes.percussion')}</option>
            <option value="bg-pink-400">{t('drumTypes.percussion')} (Medium)</option>
            <option value="bg-pink-600">{t('drumTypes.percussion')} (Dark)</option>
            <option value="bg-gray-500">{t('drumTypes.percussion')} (Gray)</option>
            <option value="bg-green-700">{t('drumTypes.kick')} (Dark)</option>
          </select>
        </div>
        <div className="flex space-x-1 justify-center">
          <button
            onClick={() => previewSample(instrument.sampleId || (instrument as any).sample, index)}
            disabled={playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`]}
            className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
              playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`]
                ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                : `${getThemeClass('button')} shadow-md hover:shadow-lg`
            }`}
            title={t('preview')}
          >
            {playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`] ? '♪' : '▶️'}
          </button>
          <button
            onClick={() => removeInstrument(index)}
            className="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 hover:scale-110 active:scale-95 text-white text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200 transform-gpu"
            title={t('remove')}
          >
            🗑️
          </button>
          <button
            onClick={() => moveInstrumentUp(index)}
            disabled={index === 0}
            className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
              index === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-sm'
                : `${getThemeClass('button')} shadow-md hover:shadow-lg`
            }`}
            title={t('moveUp')}
          >
            ⬆️
          </button>
          <button
            onClick={() => moveInstrumentDown(index)}
            disabled={index === instrumentsLength - 1}
            className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
              index === instrumentsLength - 1
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-sm'
                : `${getThemeClass('button')} shadow-md hover:shadow-lg`
            }`}
            title={t('moveDown')}
          >
            ⬇️
          </button>
          <button
            onClick={() => {
              const currentColorIndex = DRUM_TYPES.findIndex(type => type.color === instrument.color);
              const nextIndex = (currentColorIndex + 1) % DRUM_TYPES.length;
              const nextColor = DRUM_TYPES[nextIndex].color;
              updateInstrument(index, 'color', nextColor);
            }}
            className={`w-10 h-10 rounded-full border-2 border-white shadow-md hover:shadow-lg transition-all duration-200 transform-gpu ${instrument.color} hover:scale-110 active:scale-95`}
            title={`${t('color')}: ${
              instrument.color === 'bg-green-600' ? t('drumTypes.kick') :
              instrument.color === 'bg-green-700' ? `${t('drumTypes.kick')} (Dark)` :
              instrument.color === 'bg-red-600' ? t('drumTypes.snare') :
              instrument.color === 'bg-red-400' ? `${t('drumTypes.snare')} (Light)` :
              instrument.color === 'bg-yellow-600' ? t('drumTypes.hihat') :
              instrument.color === 'bg-yellow-500' ? `${t('drumTypes.hihat')} (Medium)` :
              instrument.color === 'bg-yellow-300' ? t('drumTypes.openhat') :
              instrument.color === 'bg-blue-400' ? `${t('drumTypes.tom')} (Light)` :
              instrument.color === 'bg-blue-500' ? t('drumTypes.tom') :
              instrument.color === 'bg-blue-700' ? `${t('drumTypes.tom')} (Dark)` :
              instrument.color === 'bg-purple-400' ? t('drumTypes.clap') :
              instrument.color === 'bg-purple-300' ? `${t('drumTypes.clap')} (Light)` :
              instrument.color === 'bg-orange-600' ? t('drumTypes.crash') :
              instrument.color === 'bg-orange-400' ? t('drumTypes.ride') :
              instrument.color === 'bg-orange-300' ? `${t('drumTypes.crash')} (Light)` :
              instrument.color === 'bg-pink-500' ? t('drumTypes.percussion') :
              instrument.color === 'bg-pink-400' ? `${t('drumTypes.percussion')} (Medium)` :
              instrument.color === 'bg-pink-600' ? `${t('drumTypes.percussion')} (Dark)` :
              instrument.color === 'bg-gray-500' ? `${t('drumTypes.percussion')} (Gray)` :
              instrument.color
            }`}
          >
            🎨
          </button>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden space-y-3">
        <div className="flex items-center justify-between">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white border-2 border-white shadow-md ${instrument.color}`}>{instrument.id}</div>
          <div className="flex space-x-2">
            <button
              onClick={() => previewSample(instrument.sampleId || (instrument as any).sample, index)}
              disabled={playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`]}
              className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
                playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`]
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                  : `${getThemeClass('button')} shadow-md hover:shadow-lg`
              }`}
            >
              {playingPreviews[`${instrument.sampleId || (instrument as any).sample}-${index}`] ? '♪' : '▶️'}
            </button>
            <button
              onClick={() => moveInstrumentUp(index)}
              disabled={index === 0}
              className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
                index === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-sm'
                  : `${getThemeClass('button')} shadow-md hover:shadow-lg`
              }`}
            >
              ⬆️
            </button>
            <button
              onClick={() => moveInstrumentDown(index)}
              disabled={index === instrumentsLength - 1}
              className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${
                index === instrumentsLength - 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-sm'
                  : `${getThemeClass('button')} shadow-md hover:shadow-lg`
              }`}
            >
              ⬇️
            </button>
            <button
              onClick={() => removeInstrument(index)}
              className="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 hover:scale-110 active:scale-95 text-white text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200 transform-gpu"
            >
              🗑️
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1">{t('instrumentType')}</label>
            <select
              value={instrument.type}
              onChange={(e) => updateInstrument(index, 'type', e.target.value)}
              className={`w-full border px-2 py-1 rounded text-sm ${getThemeClass('border')} ${getThemeClass('input')}`}
            >
              {DRUM_TYPES.map(type => (
                <option key={type.id} value={type.id}>{t(`drumTypes.${type.id}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">{t('instrumentId')}</label>
            <input
              type="text"
              value={instrument.id}
              onChange={(e) => updateInstrument(index, 'id', e.target.value.toUpperCase())}
              className={`w-full border px-2 py-1 rounded font-mono text-sm ${getThemeClass('border')} ${getThemeClass('input')}`}
              maxLength={3}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('sample')}</label>
          <select
            value={instrument.sampleId || (instrument as any).sample}
            onChange={(e) => updateInstrument(index, 'sampleId', e.target.value)}
            className={`w-full border px-2 py-1 rounded text-sm ${getThemeClass('border')} ${getThemeClass('input')}`}
          >
            {getSamplesByType(instrument.type).map((sample: any) => (
              <option key={sample.id} value={sample.id}>{sample.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">{t('instrumentName')}</label>
          <input
            type="text"
            value={instrument.name}
            onChange={(e) => updateInstrument(index, 'name', e.target.value)}
            className={`w-full border px-2 py-1 rounded text-sm ${getThemeClass('border')} ${getThemeClass('input')}`}
          />
        </div>
      </div>
    </div>
  );
} 